import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, InputBase } from '@mui/material';
import {
  PlayArrowRounded, ArrowForwardRounded, ArrowBackRounded, CheckRounded, FormatQuoteRounded, PhoneRounded,
  MailOutlineRounded, PlaceOutlined, KeyboardArrowDownRounded, StarRounded, MenuRounded, CalendarMonthRounded,
  AccessTimeRounded, NorthEastRounded, Instagram, Facebook, X, LinkedIn, YouTube,
  CampaignRounded, InsightsRounded, MailRounded, ApiRounded, GroupsRounded, BrushRounded, CodeRounded,
  CloudRounded, TrendingUpRounded, SearchRounded, RocketLaunchRounded, SpeedRounded, DesignServicesRounded,
  ShoppingCartRounded, StorefrontRounded, PsychologyRounded, SupportAgentRounded, HandshakeRounded,
  AccountBalanceRounded, CameraAltRounded, VideocamRounded, AutoFixHighRounded, MusicNoteRounded, AlbumRounded,
  HeadphonesRounded, MicRounded, PersonRounded, LightbulbRounded, ArchitectureRounded, HubRounded,
  PhotoRounded, TerrainRounded, LocationCityRounded, ForestRounded, EventRounded, CelebrationRounded,
  ConnectWithoutContactRounded, AutoGraphRounded, LayersRounded, PublicRounded, FormatPaintRounded,
  WebRounded, ViewQuiltRounded, TuneRounded, EmojiObjectsRounded, BoltRounded, WorkspacePremiumRounded,
} from '@mui/icons-material';

// Icons referenced by name from creativeTemplates.js.
const ICONS = {
  campaign: CampaignRounded, insights: InsightsRounded, mail: MailRounded, api: ApiRounded, team: GroupsRounded,
  brush: BrushRounded, code: CodeRounded, cloud: CloudRounded, growth: TrendingUpRounded, search: SearchRounded,
  rocket: RocketLaunchRounded, speed: SpeedRounded, design: DesignServicesRounded, cart: ShoppingCartRounded,
  store: StorefrontRounded, strategy: PsychologyRounded, support: SupportAgentRounded, handshake: HandshakeRounded,
  finance: AccountBalanceRounded, camera: CameraAltRounded, video: VideocamRounded, edit: AutoFixHighRounded,
  music: MusicNoteRounded, album: AlbumRounded, headphones: HeadphonesRounded, mic: MicRounded, person: PersonRounded,
  idea: LightbulbRounded, architecture: ArchitectureRounded, hub: HubRounded, photo: PhotoRounded,
  mountain: TerrainRounded, city: LocationCityRounded, nature: ForestRounded, event: EventRounded,
  party: CelebrationRounded, social: ConnectWithoutContactRounded, chart: AutoGraphRounded, layers: LayersRounded,
  globe: PublicRounded, paint: FormatPaintRounded, web: WebRounded, layout: ViewQuiltRounded, tune: TuneRounded,
  bulb: EmojiObjectsRounded, bolt: BoltRounded, award: WorkspacePremiumRounded,
};
const SOCIALS = [[Instagram, 'Instagram'], [Facebook, 'Facebook'], [X, 'X'], [LinkedIn, 'LinkedIn'], [YouTube, 'YouTube']];

const FONTS = {
  poppins: '"Poppins", "Inter", sans-serif',
  grotesk: '"Space Grotesk", "Poppins", sans-serif',
  syne: '"Syne", "Poppins", sans-serif',
  oswald: '"Oswald", "Poppins", sans-serif',
  serif: '"Playfair Display", Georgia, serif',
  dm: '"DM Sans", "Inter", sans-serif',
};
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;700&family=Syne:wght@600;700;800&family=Oswald:wght@500;600;700&family=DM+Sans:wght@400;500;700&display=swap';

// The extra display fonts are only needed by these templates, so they load
// the first time one is shown.
const useCreativeFonts = () => {
  useEffect(() => {
    if (document.getElementById('creative-fonts')) return;
    const l = document.createElement('link');
    l.id = 'creative-fonts';
    l.rel = 'stylesheet';
    l.href = FONT_HREF;
    document.head.appendChild(l);
  }, []);
};

// One-page creative site assembled from reusable blocks. Each Creative
// template in creativeTemplates.js lists its blocks in the order of the Phlox
// demo it is modelled on, with our own text and stand-in artwork.
const CreativeSite = ({ template, onAction, preview = false }) => {
  useCreativeFonts();
  const { primary, accent, bg } = template.colors;
  const c = template.creative;
  const dark = !!c.dark;
  const fg = dark ? '#ffffff' : primary;
  const font = FONTS[c.font] || FONTS.poppins;
  const body = FONTS[c.body] || FONTS.poppins;
  const radius = c.round ?? 16;
  const upper = !!c.upper;
  const act = onAction || (() => {});
  const sid = (id) => (preview ? undefined : id);
  const action = (href) => (preview ? { component: 'span' } : href ? { href } : { onClick: act });
  const link = (id) => (preview ? { component: 'span' } : { component: 'a', href: `#${id}` });
  const accentText = c.accentText || (dark ? '#0b0b0b' : '#ffffff');
  const surface = dark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const line = dark ? 'rgba(255,255,255,0.12)' : `${primary}14`;

  const inner = { maxWidth: 1200, mx: 'auto', position: 'relative' };
  const pad = { px: { xs: 3, md: 8 }, py: { xs: 7, md: 11 }, scrollMarginTop: 60 };
  const eyebrowSx = { color: accent, fontWeight: 600, letterSpacing: 2.5, textTransform: 'uppercase', fontSize: 13, mb: 1.5, fontFamily: body };
  const h2 = { fontFamily: font, fontWeight: c.weight || 700, fontSize: { xs: 30, md: 46 }, lineHeight: 1.12, letterSpacing: c.font === 'serif' ? 0 : '-0.02em', textTransform: upper ? 'uppercase' : 'none' };
  const solid = { bgcolor: accent, color: accentText, px: 3.5, py: 1.3, borderRadius: c.btnRound ?? 999, fontWeight: 600, fontFamily: body, textTransform: upper ? 'uppercase' : 'none', letterSpacing: upper ? 1 : 0, '&:hover': { bgcolor: accent, opacity: 0.9 } };
  const ghost = { color: 'inherit', border: '1.5px solid currentColor', px: 3.5, py: 1.2, borderRadius: c.btnRound ?? 999, fontWeight: 600, fontFamily: body, textTransform: upper ? 'uppercase' : 'none', letterSpacing: upper ? 1 : 0 };
  const muted = { opacity: dark ? 0.7 : 0.68, lineHeight: 1.75 };

  // Section wrapper. tone: undefined (page), 'alt' (tinted), 'band' (primary
  // colour), 'accent', 'light' (white on dark sites) or 'black'.
  const toneSx = (tone) => {
    if (tone === 'alt') return { bgcolor: dark ? 'rgba(255,255,255,0.03)' : `color-mix(in srgb, ${accent} 6%, ${bg})` };
    if (tone === 'band') return { bgcolor: dark ? '#000' : primary, color: '#fff' };
    if (tone === 'accent') return { bgcolor: accent, color: accentText };
    if (tone === 'white') return { bgcolor: '#fff', color: primary };
    return {};
  };
  const isDarkTone = (tone) => (tone === 'band' ? true : tone === 'white' ? false : tone === 'accent' ? accentText === '#ffffff' : dark);

  // Stand-in artwork: a soft gradient tile with an icon, so templates ship
  // without third-party photos.
  const Art = ({ i = 0, h = 240, icon, shade, round, sx, children }) => {
    const Icon = ICONS[icon] || ICONS[c.icon] || PhotoRounded;
    const deep = shade ?? dark;
    const ang = 135 + i * 41;
    return (
      <Box sx={{
        height: h, borderRadius: round ?? (radius ? `${Math.max(radius - 4, 0)}px` : 0), position: 'relative', overflow: 'hidden',
        display: 'grid', placeItems: 'center', flexShrink: 0,
        background: deep
          ? `linear-gradient(${ang}deg, color-mix(in srgb, ${accent} ${30 + (i % 3) * 12}%, #111) 0%, #141414 100%)`
          : `linear-gradient(${ang}deg, color-mix(in srgb, ${accent} ${22 + (i % 3) * 10}%, #fff) 0%, color-mix(in srgb, ${primary} ${8 + (i % 4) * 5}%, #fff) 100%)`,
        ...sx,
      }}>
        <Box sx={{ position: 'absolute', width: '70%', aspectRatio: '1', borderRadius: '50%', right: '-18%', bottom: '-25%', bgcolor: deep ? '#ffffff08' : '#ffffff55' }} />
        <Icon sx={{ fontSize: typeof h === 'number' ? Math.max(28, Math.min(96, h / 3)) : 64, color: deep ? '#ffffff40' : `color-mix(in srgb, ${primary} 35%, transparent)`, position: 'relative' }} />
        {children}
      </Box>
    );
  };

  const Heading = ({ s, center = true, light }) => (
    <Box sx={{ textAlign: center ? 'center' : 'left', mb: { xs: 4, md: 6 }, maxWidth: center ? 720 : 'none', mx: center ? 'auto' : 0 }}>
      {s.eyebrow && <Typography sx={eyebrowSx}>{s.eyebrow}</Typography>}
      {s.title && <Typography component="h2" sx={h2}>{s.title}</Typography>}
      {s.subtitle && <Typography sx={{ ...muted, mt: 1.5, color: light ? '#fff' : undefined }}>{s.subtitle}</Typography>}
    </Box>
  );

  const HeadRow = ({ s }) => (
    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'flex-end' }, justifyContent: 'space-between', gap: 3, mb: { xs: 4, md: 6 }, flexDirection: { xs: 'column', md: 'row' } }}>
      <Box sx={{ maxWidth: 640 }}>
        {s.eyebrow && <Typography sx={eyebrowSx}>{s.eyebrow}</Typography>}
        <Typography component="h2" sx={h2}>{s.title}</Typography>
        {s.subtitle && <Typography sx={{ ...muted, mt: 1.5 }}>{s.subtitle}</Typography>}
      </Box>
      {s.cta && <Button {...action()} sx={ghost} endIcon={<ArrowForwardRounded />}>{s.cta}</Button>}
    </Box>
  );

  const Socials = ({ n = 4, size = 36, filled }) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {SOCIALS.slice(0, n).map(([Icon, label]) => (
        <Box key={label} aria-label={label} sx={{ width: size, height: size, borderRadius: '50%', display: 'grid', placeItems: 'center', border: filled ? 0 : '1px solid currentColor', bgcolor: filled ? accent : 'transparent', color: filled ? accentText : 'inherit', opacity: filled ? 1 : 0.75 }}>
          <Icon sx={{ fontSize: size * 0.45 }} />
        </Box>
      ))}
    </Box>
  );

  const Email = ({ cta = 'Subscribe', light, maxWidth = 480, center = true }) => (
    <Box sx={{ display: 'flex', gap: 1, maxWidth, mx: center ? 'auto' : 0, p: 0.75, borderRadius: c.btnRound ?? 999, bgcolor: light ? 'rgba(255,255,255,0.1)' : '#fff', border: `1px solid ${light ? 'rgba(255,255,255,0.2)' : `${primary}1a`}` }}>
      <InputBase placeholder="Your email address" inputProps={{ 'aria-label': 'Email address', tabIndex: preview ? -1 : 0 }}
        sx={{ flex: 1, px: 2, fontSize: 15, fontFamily: body, color: light ? '#fff' : primary }} />
      <Button {...action()} sx={{ ...solid, py: 1, whiteSpace: 'nowrap' }}>{cta}</Button>
    </Box>
  );

  const Play = ({ size = 76 }) => (
    <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
      <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: accent, color: accentText, display: 'grid', placeItems: 'center', boxShadow: `0 0 0 12px color-mix(in srgb, ${accent} 25%, transparent)` }}>
        <PlayArrowRounded sx={{ fontSize: size * 0.5 }} />
      </Box>
    </Box>
  );

  const Dots = ({ n = 3, light }) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {Array.from({ length: n }).map((_, d) => <Box key={d} sx={{ width: d ? 10 : 30, height: 10, borderRadius: 999, bgcolor: d ? (light ? '#ffffff40' : `${primary}26`) : accent }} />)}
    </Box>
  );

  // Collage of stand-in images for hero and split blocks.
  const Collage = ({ kind = 'single', h: H = 460, icons = [] }) => {
    const h = typeof H === 'number' ? H : 560;
    if (kind === 'cutout') {
      return (
        <Box sx={{ position: 'relative', height: h }}>
          <Box sx={{ position: 'absolute', inset: '12% 6% 0 6%', borderRadius: '50% 50% 0 0', background: `linear-gradient(160deg, ${accent} 0%, color-mix(in srgb, ${accent} 40%, ${dark ? '#000' : primary}) 100%)` }} />
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'end center' }}>
            <PersonRounded sx={{ fontSize: h * 0.85, color: dark ? '#ffffff26' : '#ffffff8c', mb: -4 }} />
          </Box>
          {icons.length > 0 && icons.map((ic, k) => {
            const Ic = ICONS[ic] || BoltRounded;
            const pos = [{ top: '8%', left: 0 }, { top: '30%', right: 0 }, { bottom: '22%', left: '-2%' }, { bottom: '6%', right: '6%' }, { top: '-2%', right: '24%' }][k % 5];
            return (
              <Box key={ic + k} sx={{ position: 'absolute', ...pos, width: 64, height: 64, borderRadius: 3, bgcolor: dark ? '#1c1c1c' : '#fff', boxShadow: '0 14px 30px rgba(0,0,0,0.12)', display: 'grid', placeItems: 'center' }}>
                <Ic sx={{ color: accent, fontSize: 30 }} />
              </Box>
            );
          })}
        </Box>
      );
    }
    if (kind === 'two') {
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, alignItems: 'end', height: h }}>
          <Art i={1} h={h * 0.85} icon={icons[0]} />
          <Art i={3} h={h} icon={icons[1]} sx={{ mb: 6 }} />
        </Box>
      );
    }
    if (kind === 'stack') {
      return (
        <Box sx={{ position: 'relative', height: h }}>
          <Art i={2} h="78%" icon={icons[0]} sx={{ width: '78%' }} />
          <Art i={5} h="52%" icon={icons[1]} sx={{ position: 'absolute', right: 0, bottom: 0, width: '52%', border: `8px solid ${bg}` }} />
        </Box>
      );
    }
    if (kind === 'mosaic') {
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gridAutoRows: h / 4 - 10, gap: 1.5 }}>
          {[0, 1, 2, 3, 4, 5, 6, 7].map(k => <Art key={k} i={k} h="100%" icon={icons[k % (icons.length || 1)]} sx={{ gridColumn: k === 0 || k === 5 ? 'span 2' : undefined, gridRow: k === 0 ? 'span 2' : undefined }} />)}
        </Box>
      );
    }
    if (kind === 'quad') {
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, height: h }}>
          <Art i={0} h="100%" icon={icons[0]} sx={{ gridRow: 'span 2' }} />
          <Art i={2} h="100%" icon={icons[1]} />
          <Art i={4} h="100%" icon={icons[2]} />
        </Box>
      );
    }
    if (kind === 'circle') {
      return (
        <Box sx={{ position: 'relative', height: h, display: 'grid', placeItems: 'center' }}>
          <Box sx={{ position: 'absolute', width: '88%', aspectRatio: '1', borderRadius: '50%', border: `2px dashed color-mix(in srgb, ${accent} 50%, transparent)` }} />
          <Art i={1} h={h * 0.78} round="50%" icon={icons[0]} sx={{ aspectRatio: '1', width: 'auto' }} />
          {icons.slice(1).map((ic, k) => {
            const Ic = ICONS[ic] || BoltRounded;
            const pos = [{ top: '6%', right: '10%' }, { bottom: '10%', left: '6%' }, { top: '42%', left: '-2%' }][k % 3];
            return (
              <Box key={ic + k} sx={{ position: 'absolute', ...pos, px: 2, py: 1.5, borderRadius: 3, bgcolor: dark ? '#1b1b1b' : '#fff', boxShadow: '0 16px 36px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Ic sx={{ color: accent }} />
              </Box>
            );
          })}
        </Box>
      );
    }
    return <Art i={2} h={h} icon={icons[0]} />;
  };

  const Num = ({ children }) => (
    <Typography sx={{ fontFamily: font, fontWeight: 700, color: accent, fontSize: 15, letterSpacing: 1 }}>{children}</Typography>
  );

  const render = (s, idx) => {
    const key = `${s.type}-${idx}`;
    const tone = toneSx(s.tone);
    const onDark = isDarkTone(s.tone);

    switch (s.type) {
      case 'topbar':
        return (
          <Box key={key} sx={{ bgcolor: dark ? '#000' : primary, color: '#fff', px: { xs: 3, md: 8 }, py: 1, fontSize: 13, fontFamily: body }}>
            <Box sx={{ ...inner, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 3, opacity: 0.85, flexWrap: 'wrap' }}>
                {s.items.map(t => <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><PhoneRounded sx={{ fontSize: 15 }} />{t}</Box>)}
              </Box>
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1.5, opacity: 0.85 }}>
                {SOCIALS.slice(0, 3).map(([Icon, label]) => <Icon key={label} sx={{ fontSize: 16 }} />)}
              </Box>
            </Box>
          </Box>
        );

      case 'nav': {
        const overlay = s.overlay;
        return (
          <Box key={key} sx={{
            px: { xs: 3, md: 8 }, py: 2.5, fontFamily: body, color: overlay ? '#fff' : fg,
            bgcolor: overlay ? 'transparent' : s.tone === 'band' ? primary : bg,
            position: overlay ? 'absolute' : 'relative', left: 0, right: 0, zIndex: 3,
            borderBottom: s.border ? `1px solid ${line}` : 0, ...(s.tone === 'band' ? { color: '#fff' } : {}),
          }}>
            <Box sx={{ ...inner, display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: s.center ? 1 : 'none', minWidth: 0 }}>
                {c.logoMark !== false && (
                  <Box sx={{ width: 34, height: 34, borderRadius: c.logoShape === 'square' ? 1 : '50%', bgcolor: accent, color: accentText, display: 'grid', placeItems: 'center', fontWeight: 800, fontFamily: font, fontSize: 17 }}>
                    {c.brand[0]}
                  </Box>
                )}
                <Typography noWrap sx={{ fontWeight: 800, fontSize: 21, fontFamily: font, letterSpacing: upper ? 1.5 : '-0.01em', textTransform: upper ? 'uppercase' : 'none' }}>{c.brand}</Typography>
              </Box>
              <Box sx={{ flex: s.center ? 'none' : 1, display: { xs: 'none', md: 'flex' }, justifyContent: s.center ? 'center' : 'flex-end', gap: 4, pr: s.center ? 0 : 2 }}>
                {s.links.map(([label, id], k) => (
                  <Typography key={label} {...link(id)} sx={{ color: 'inherit', textDecoration: 'none', fontSize: 14.5, fontWeight: 500, opacity: k === 0 ? 1 : 0.75, textTransform: upper ? 'uppercase' : 'none', letterSpacing: upper ? 1.2 : 0, '&:hover': { opacity: 1, color: accent } }}>
                    {label}
                  </Typography>
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: s.center ? 1 : 'none', justifyContent: 'flex-end' }}>
                {s.phone && (
                  <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: '50%', bgcolor: `color-mix(in srgb, ${accent} 15%, transparent)`, display: 'grid', placeItems: 'center' }}><PhoneRounded sx={{ color: accent, fontSize: 18 }} /></Box>
                    <Box><Typography sx={{ fontSize: 11.5, opacity: 0.6 }}>Free call</Typography><Typography sx={{ fontSize: 14, fontWeight: 700 }}>{s.phone}</Typography></Box>
                  </Box>
                )}
                {s.socials && <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.5, opacity: 0.8 }}>{SOCIALS.slice(0, 4).map(([Icon, label]) => <Icon key={label} sx={{ fontSize: 18 }} />)}</Box>}
                {s.cta && <Button {...action()} size="small" sx={{ ...solid, py: 0.9, px: 2.75, display: { xs: 'none', sm: 'inline-flex' } }}>{s.cta}</Button>}
                {s.menu && <MenuRounded sx={{ fontSize: 28 }} />}
              </Box>
            </Box>
          </Box>
        );
      }

      case 'hero':
        return renderHero(s, key);

      case 'text':
        return (
          <Box key={key} component="section" id={sid(s.id)} sx={{ ...pad, ...tone, py: { xs: 6, md: s.compact ? 6 : 10 } }}>
            <Box sx={{ ...inner, maxWidth: s.wide ? 1100 : 860, textAlign: s.left ? 'left' : 'center' }}>
              {s.eyebrow && <Typography sx={eyebrowSx}>{s.eyebrow}</Typography>}
              <Typography component="h2" sx={{ ...h2, fontSize: s.big ? { xs: 34, md: 60 } : h2.fontSize }}>{s.title}</Typography>
              {s.text && <Typography sx={{ ...muted, mt: 2.5, fontSize: 17, maxWidth: 720, mx: s.left ? 0 : 'auto' }}>{s.text}</Typography>}
              {s.quote && <Typography sx={{ fontFamily: font, fontSize: { xs: 20, md: 26 }, mt: 4, lineHeight: 1.5, fontWeight: 500, maxWidth: 860, mx: s.left ? 0 : 'auto', color: accent }}>{s.quote}</Typography>}
              {s.cta && <Button {...action()} sx={{ ...solid, mt: 4 }}>{s.cta}</Button>}
            </Box>
          </Box>
        );

      case 'split': {
        const art = (
          <Box sx={{ position: 'relative' }}>
            <Collage kind={s.art || 'single'} h={s.h || 460} icons={s.icons || []} />
            {s.badge && (
              <Box sx={{ position: 'absolute', left: { xs: 12, md: -24 }, bottom: 28, bgcolor: accent, color: accentText, px: 3, py: 2.25, borderRadius: 3, boxShadow: '0 20px 40px rgba(0,0,0,0.18)' }}>
                <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: 36, lineHeight: 1 }}>{s.badge[0]}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.5, maxWidth: 140 }}>{s.badge[1]}</Typography>
              </Box>
            )}
            {s.video && <Play />}
          </Box>
        );
        return (
          <Box key={key} component="section" id={sid(s.id)} sx={{ ...pad, ...tone }}>
            <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 5, md: 10 }, alignItems: 'center' }}>
              <Box sx={{ order: { xs: 0, md: s.reverse ? 1 : 0 } }}>{art}</Box>
              <Box>
                {s.eyebrow && <Typography sx={eyebrowSx}>{s.eyebrow}</Typography>}
                <Typography component="h2" sx={h2}>{s.title}</Typography>
                {s.text && <Typography sx={{ ...muted, mt: 2.5 }}>{s.text}</Typography>}
                {s.text2 && <Typography sx={{ ...muted, mt: 2 }}>{s.text2}</Typography>}
                {s.points && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: s.points.length > 3 ? { xs: '1fr', sm: '1fr 1fr' } : '1fr', gap: 1.5, mt: 3.5 }}>
                    {s.points.map(p => (
                      <Box key={p} sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                        <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: `color-mix(in srgb, ${accent} 18%, transparent)`, display: 'grid', placeItems: 'center', flexShrink: 0 }}><CheckRounded sx={{ fontSize: 16, color: accent }} /></Box>
                        <Typography sx={{ fontWeight: 600, fontSize: 15.5 }}>{p}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {s.features && (
                  <Box sx={{ display: 'grid', gap: 3, mt: 4 }}>
                    {s.features.map(([title, text, icon]) => {
                      const Ic = ICONS[icon] || BoltRounded;
                      return (
                        <Box key={title} sx={{ display: 'flex', gap: 2.25 }}>
                          <Box sx={{ width: 54, height: 54, borderRadius: 3, flexShrink: 0, bgcolor: `color-mix(in srgb, ${accent} 14%, transparent)`, display: 'grid', placeItems: 'center' }}><Ic sx={{ color: accent }} /></Box>
                          <Box><Typography sx={{ fontWeight: 700, fontSize: 18, fontFamily: font, mb: 0.5 }}>{title}</Typography><Typography sx={{ ...muted, fontSize: 15 }}>{text}</Typography></Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {s.stat && (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 4 }}>
                    <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: 52, color: accent, lineHeight: 1 }}>{s.stat[0]}</Typography>
                    <Typography sx={{ fontWeight: 600, maxWidth: 180, lineHeight: 1.4 }}>{s.stat[1]}</Typography>
                  </Box>
                )}
                {(s.cta || s.cta2) && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 4.5, flexWrap: 'wrap' }}>
                    {s.cta && <Button {...action()} sx={solid}>{s.cta}</Button>}
                    {s.cta2 && <Button {...action()} sx={ghost}>{s.cta2}</Button>}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        );
      }

      case 'logos':
        return (
          <Box key={key} component="section" sx={{ ...pad, ...tone, py: { xs: 5, md: 7 } }}>
            <Box sx={inner}>
              {s.title && <Typography sx={{ textAlign: 'center', fontWeight: 600, mb: 4, opacity: 0.7, fontFamily: body }}>{s.title}</Typography>}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: `repeat(${s.items.length}, 1fr)` }, gap: { xs: 3, md: 4 }, alignItems: 'center' }}>
                {s.items.map(n => (
                  <Typography key={n} sx={{ textAlign: 'center', fontFamily: font, fontWeight: 800, fontSize: { xs: 17, md: 22 }, letterSpacing: '-0.02em', opacity: 0.4, whiteSpace: 'nowrap' }}>{n}</Typography>
                ))}
              </Box>
            </Box>
          </Box>
        );

      case 'services': {
        const style = s.style || 'card';
        const cols = s.cols || s.items.length;
        return (
          <Box key={key} component="section" id={sid(s.id || 'services')} sx={{ ...pad, ...tone }}>
            <Box sx={inner}>
              {s.row ? <HeadRow s={s} /> : <Heading s={s} />}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${cols}, 1fr)` }, gap: style === 'links' ? 0 : 3 }}>
                {s.items.map((it, k) => {
                  const Ic = ICONS[it.icon] || BoltRounded;
                  if (style === 'image') {
                    return (
                      <Box key={it.title} sx={{ bgcolor: onDark ? 'rgba(255,255,255,0.05)' : surface, borderRadius: `${radius}px`, overflow: 'hidden', boxShadow: onDark ? 'none' : `0 12px 36px ${primary}0f` }}>
                        <Art i={k} h={s.tall ? 360 : 200} icon={it.icon} round={0} />
                        <Box sx={{ p: 3 }}>
                          {s.numbered && <Num>.{String(s.items.length - k).padStart(2, '0')}</Num>}
                          <Typography sx={{ fontWeight: 700, fontSize: 20, fontFamily: font, my: 1 }}>{it.title}</Typography>
                          <Typography sx={{ ...muted, fontSize: 15 }}>{it.text}</Typography>
                          {s.more && <Typography sx={{ mt: 2, fontWeight: 700, color: accent, fontSize: 14, display: 'flex', alignItems: 'center', gap: 0.5 }}>{s.more} <ArrowForwardRounded sx={{ fontSize: 16 }} /></Typography>}
                        </Box>
                      </Box>
                    );
                  }
                  if (style === 'links') {
                    return (
                      <Box key={it.title} sx={{ p: 3.5, borderTop: `1px solid ${line}`, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Num>{String(k + 1).padStart(2, '0')}</Num>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 20, fontFamily: font, mb: 0.75 }}>{it.title}</Typography>
                          <Typography sx={{ ...muted, fontSize: 14.5 }}>{it.text}</Typography>
                        </Box>
                        <NorthEastRounded sx={{ color: accent }} />
                      </Box>
                    );
                  }
                  if (style === 'tall') {
                    return (
                      <Art key={it.title} i={k * 2} h={460} icon={it.icon} shade sx={{ placeItems: 'end start' }}>
                        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 35%, rgba(0,0,0,0.75) 100%)' }} />
                        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: 3.5, color: '#fff', textAlign: 'left' }}>
                          <Ic sx={{ color: accent, fontSize: 36, mb: 1 }} />
                          <Typography sx={{ fontWeight: 700, fontSize: 26, fontFamily: font, textTransform: upper ? 'uppercase' : 'none' }}>{it.title}</Typography>
                          <Typography sx={{ opacity: 0.75, fontSize: 15, mt: 0.75 }}>{it.text}</Typography>
                        </Box>
                      </Art>
                    );
                  }
                  return (
                    <Box key={it.title} sx={{
                      p: { xs: 3.5, md: 4 }, borderRadius: `${radius}px`, textAlign: s.center ? 'center' : 'left',
                      bgcolor: s.outline ? 'transparent' : onDark ? 'rgba(255,255,255,0.05)' : surface,
                      border: s.outline ? `1px solid ${line}` : 0, boxShadow: s.outline || onDark ? 'none' : `0 14px 40px ${primary}10`,
                      transition: 'transform .25s', '&:hover': { transform: 'translateY(-6px)' },
                    }}>
                      <Box sx={{ width: 64, height: 64, borderRadius: s.square ? 3 : '50%', bgcolor: k === (s.highlight ?? -1) ? accent : `color-mix(in srgb, ${accent} 14%, transparent)`, display: 'grid', placeItems: 'center', mb: 3, mx: s.center ? 'auto' : 0 }}>
                        <Ic sx={{ color: k === (s.highlight ?? -1) ? accentText : accent, fontSize: 30 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 20, fontFamily: font, mb: 1.25 }}>{it.title}</Typography>
                      <Typography sx={{ ...muted, fontSize: 15 }}>{it.text}</Typography>
                      {s.more && <Typography sx={{ mt: 2.5, fontWeight: 700, color: accent, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>{s.more} <ArrowForwardRounded sx={{ fontSize: 16 }} /></Typography>}
                    </Box>
                  );
                })}
              </Box>
              {s.cta && !s.row && <Box sx={{ textAlign: 'center', mt: 6 }}><Button {...action()} sx={ghost}>{s.cta}</Button></Box>}
            </Box>
          </Box>
        );
      }

      case 'portfolio': {
        const style = s.style || 'grid';
        const cols = s.cols || 3;
        return (
          <Box key={key} component="section" id={sid(s.id || 'work')} sx={{ ...pad, ...tone }}>
            <Box sx={{ ...inner, maxWidth: s.full ? 1400 : 1200 }}>
              {s.title && (s.row ? <HeadRow s={s} /> : <Heading s={s} />)}
              {s.filters && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 3 }, justifyContent: s.row ? 'flex-start' : 'center', mb: 5 }}>
                  {s.filters.map((f, k) => (
                    <Typography key={f} sx={{ fontWeight: 600, fontSize: 14.5, textTransform: upper || s.upperFilters ? 'uppercase' : 'capitalize', letterSpacing: 1, pb: 0.5, cursor: 'pointer', color: k === 0 ? accent : 'inherit', opacity: k === 0 ? 1 : 0.6, borderBottom: `2px solid ${k === 0 ? accent : 'transparent'}` }}>{f}</Typography>
                  ))}
                </Box>
              )}
              {style === 'list' ? (
                <Box>
                  {s.items.map((it, k) => (
                    <Box key={it.title} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '120px 1fr auto' }, gap: { xs: 2, md: 4 }, alignItems: 'center', py: 3.5, borderTop: `1px solid ${line}`, '&:last-of-type': { borderBottom: `1px solid ${line}` } }}>
                      <Art i={k} h={84} icon={it.icon} />
                      <Box>
                        <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: { xs: 24, md: 36 }, letterSpacing: '-0.02em' }}>{it.title}</Typography>
                        <Typography sx={{ color: accent, fontWeight: 600, fontSize: 14 }}>{it.tag}</Typography>
                      </Box>
                      <Box sx={{ width: 54, height: 54, borderRadius: '50%', border: `1px solid ${line}`, display: { xs: 'none', md: 'grid' }, placeItems: 'center' }}><NorthEastRounded /></Box>
                    </Box>
                  ))}
                </Box>
              ) : style === 'carousel' ? (
                <Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2 }}>
                    {s.items.map((it, k) => (
                      <Art key={it.title} i={k} h={s.h || 360} icon={it.icon} sx={{ display: { xs: k > 1 ? 'none' : 'grid', md: 'grid' } }} />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, mt: 4, justifyContent: 'center' }}>
                    {[ArrowBackRounded, ArrowForwardRounded].map((Ic, k) => (
                      <Box key={k} sx={{ width: 48, height: 48, borderRadius: '50%', border: `1px solid ${line}`, display: 'grid', placeItems: 'center', bgcolor: k ? accent : 'transparent', color: k ? accentText : 'inherit' }}><Ic /></Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${cols}, 1fr)` }, gridAutoFlow: 'dense', gap: s.gap ?? 3 }}>
                  {s.items.map((it, k) => {
                    const big = s.featured && s.featured.includes(k);
                    return (
                      <Box key={it.title + k} sx={{ gridColumn: big ? 'span 2' : undefined, gridRow: s.tallIdx && s.tallIdx.includes(k) ? 'span 2' : undefined }}>
                        <Art i={k} h={s.tallIdx && s.tallIdx.includes(k) ? (s.h || 260) * 2 + 24 + (s.captions === false ? 0 : 60) : s.h || 260} icon={it.icon} sx={{ '&:hover .cap': { opacity: 1 } }}>
                          {style === 'overlay' && (
                            <Box className="cap" sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.7) 100%)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', p: 3, color: '#fff', textAlign: 'left' }}>
                              <Typography sx={{ fontSize: 13, color: accent, fontWeight: 600 }}>{it.tag}</Typography>
                              <Typography sx={{ fontWeight: 700, fontSize: 20, fontFamily: font }}>{it.title}</Typography>
                            </Box>
                          )}
                          {s.numbered && <Typography sx={{ position: 'absolute', top: 18, left: 22, fontFamily: font, fontWeight: 800, fontSize: 30, color: '#ffffffcc' }}>{String(k + 1).padStart(2, '0')}</Typography>}
                        </Art>
                        {style !== 'overlay' && s.captions !== false && (
                          <Box sx={{ pt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 18, fontFamily: font }}>{it.title}</Typography>
                            {it.tag && <Typography sx={{ fontSize: 13.5, opacity: 0.6, whiteSpace: 'nowrap' }}>{it.tag}</Typography>}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
              {s.cta && !s.row && <Box sx={{ textAlign: 'center', mt: 6 }}><Button {...action()} sx={s.solidCta ? solid : ghost}>{s.cta}</Button></Box>}
            </Box>
          </Box>
        );
      }

      case 'stats':
        return (
          <Box key={key} component="section" sx={{ ...pad, ...tone, py: { xs: 6, md: 8 } }}>
            <Box sx={inner}>
              {s.title && <Heading s={s} />}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${s.items.length}, 1fr)` }, gap: 4 }}>
                {s.items.map(([v, label], k) => (
                  <Box key={label} sx={{ textAlign: 'center', borderLeft: { md: k && s.dividers ? `1px solid ${line}` : 0 } }}>
                    <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: { xs: 40, md: 56 }, color: s.tone === 'accent' ? 'inherit' : accent, lineHeight: 1.05 }}>{v}</Typography>
                    <Typography sx={{ mt: 1, fontWeight: 500, opacity: 0.75, textTransform: upper ? 'uppercase' : 'none', letterSpacing: upper ? 1.5 : 0, fontSize: 15 }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );

      case 'video':
        return (
          <Box key={key} component="section" sx={{ ...pad, ...tone, py: { xs: 5, md: 8 } }}>
            <Box sx={inner}>
              {s.title && <Heading s={s} />}
              <Art i={3} h={s.h || 520} icon={s.icon || 'video'} shade={s.shade} sx={{ borderRadius: `${radius + 4}px` }}>
                <Play size={92} />
                {s.caption && <Typography sx={{ position: 'absolute', left: 32, bottom: 28, color: '#fff', fontFamily: font, fontWeight: 700, fontSize: { xs: 22, md: 34 }, maxWidth: 520, textAlign: 'left' }}>{s.caption}</Typography>}
              </Art>
            </Box>
          </Box>
        );

      case 'process':
        return (
          <Box key={key} component="section" sx={{ ...pad, ...tone }}>
            <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: s.art ? '1fr 1fr' : '1fr' }, gap: { xs: 5, md: 10 }, alignItems: 'center' }}>
              {s.art && <Collage kind={s.art} h={480} icons={s.icons || []} />}
              <Box>
                <Heading s={s} center={!s.art} />
                <Box sx={{ display: 'grid', gridTemplateColumns: s.art ? '1fr' : { xs: '1fr', md: `repeat(${s.items.length}, 1fr)` }, gap: s.art ? 3.5 : 5 }}>
                  {s.items.map(([title, text, icon], k) => {
                    const Ic = ICONS[icon] || BoltRounded;
                    return (
                      <Box key={title} sx={{ display: 'flex', flexDirection: s.art ? 'row' : 'column', alignItems: s.art ? 'flex-start' : 'center', textAlign: s.art ? 'left' : 'center', gap: 2.5, position: 'relative' }}>
                        <Box sx={{ position: 'relative', flexShrink: 0 }}>
                          <Box sx={{ width: s.art ? 64 : 96, height: s.art ? 64 : 96, borderRadius: '50%', border: `2px solid color-mix(in srgb, ${accent} 40%, transparent)`, display: 'grid', placeItems: 'center' }}><Ic sx={{ color: accent, fontSize: s.art ? 28 : 40 }} /></Box>
                          <Box sx={{ position: 'absolute', top: -6, right: -6, width: 28, height: 28, borderRadius: '50%', bgcolor: accent, color: accentText, fontSize: 12, fontWeight: 800, display: 'grid', placeItems: 'center' }}>{String(k + 1).padStart(2, '0')}</Box>
                        </Box>
                        <Box><Typography sx={{ fontWeight: 700, fontSize: 20, fontFamily: font, mb: 1 }}>{title}</Typography><Typography sx={{ ...muted, fontSize: 15 }}>{text}</Typography></Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </Box>
        );

      case 'team':
        return (
          <Box key={key} component="section" id={sid(s.id || 'team')} sx={{ ...pad, ...tone }}>
            <Box sx={inner}>
              {s.row ? <HeadRow s={s} /> : <Heading s={s} />}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${s.items.length + (s.more ? 1 : 0)}, 1fr)` }, gap: 3 }}>
                {s.items.map(([name, role], k) => (
                  <Box key={name} sx={{ textAlign: 'center' }}>
                    {s.round ? (
                      <Art i={k} h="auto" icon="person" round="50%" sx={{ aspectRatio: '1', mb: 2.5, border: `4px solid color-mix(in srgb, ${accent} 35%, transparent)` }} />
                    ) : (
                      <Art i={k} h={320} icon="person" sx={{ mb: 2.5 }}>
                        {s.socials && <Box sx={{ position: 'absolute', bottom: 16, left: 0, right: 0, display: 'flex', justifyContent: 'center', color: '#fff' }}><Socials n={3} size={34} filled /></Box>}
                      </Art>
                    )}
                    <Typography sx={{ fontWeight: 700, fontSize: 19, fontFamily: font }}>{name}</Typography>
                    <Typography sx={{ color: accent, fontSize: 14, fontWeight: 500, mt: 0.5, textTransform: upper ? 'uppercase' : 'none' }}>{role}</Typography>
                  </Box>
                ))}
                {s.more && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ aspectRatio: '1', borderRadius: '50%', bgcolor: accent, color: accentText, display: 'grid', placeItems: 'center', mb: 2.5, border: `4px solid color-mix(in srgb, ${accent} 35%, transparent)` }}>
                      <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: 40 }}>{s.more[0]}</Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 19, fontFamily: font }}>{s.more[1]}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        );

      case 'testimonials': {
        if (s.style === 'single') {
          const [name, role, quote] = s.items[0];
          return (
            <Box key={key} component="section" sx={{ ...pad, ...tone }}>
              <Box sx={{ ...inner, maxWidth: 900, textAlign: 'center' }}>
                {s.title && <Heading s={s} />}
                <FormatQuoteRounded sx={{ fontSize: 64, color: accent }} />
                <Typography sx={{ fontFamily: font, fontSize: { xs: 22, md: 30 }, lineHeight: 1.5, fontWeight: 500, mb: 4 }}>{quote}</Typography>
                <Art i={2} h={84} icon="person" round="50%" sx={{ width: 84, mx: 'auto', mb: 2 }} />
                <Typography sx={{ fontWeight: 700, fontSize: 18, textTransform: upper ? 'uppercase' : 'none' }}>{name}</Typography>
                <Typography sx={{ color: accent, fontSize: 14 }}>{role}</Typography>
                {s.logo && <Typography sx={{ mt: 3, fontFamily: font, fontWeight: 800, fontSize: 22, opacity: 0.4 }}>{s.logo}</Typography>}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><Dots n={s.dots || 3} light={onDark} /></Box>
              </Box>
            </Box>
          );
        }
        return (
          <Box key={key} component="section" id={sid('reviews')} sx={{ ...pad, ...tone }}>
            <Box sx={inner}>
              <Heading s={s} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${Math.min(s.items.length, 3)}, 1fr)` }, gap: 3 }}>
                {s.items.map(([name, role, quote], k) => (
                  <Box key={name} sx={{ p: { xs: 3.5, md: 4 }, borderRadius: `${radius}px`, bgcolor: onDark ? 'rgba(255,255,255,0.05)' : surface, border: `1px solid ${onDark ? 'rgba(255,255,255,0.1)' : `${primary}0d`}`, boxShadow: onDark ? 'none' : `0 14px 40px ${primary}0d`, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', gap: 0.25 }}>{[0, 1, 2, 3, 4].map(st => <StarRounded key={st} sx={{ fontSize: 18, color: '#f5b301' }} />)}</Box>
                      <FormatQuoteRounded sx={{ fontSize: 40, color: accent, opacity: 0.8 }} />
                    </Box>
                    <Typography sx={{ ...muted, flex: 1, fontSize: 15.5 }}>{quote}</Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 3 }}>
                      <Art i={k + 2} h={50} icon="person" round="50%" sx={{ width: 50 }} />
                      <Box><Typography sx={{ fontWeight: 700 }}>{name}</Typography><Typography sx={{ fontSize: 13.5, opacity: 0.6 }}>{role}</Typography></Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );
      }

      case 'pricing':
        return (
          <Box key={key} component="section" id={sid('pricing')} sx={{ ...pad, ...tone }}>
            <Box sx={inner}>
              <Heading s={s} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${s.items.length}, 1fr)` }, gap: 3, alignItems: 'center' }}>
                {s.items.map((p) => {
                  const hi = p.highlight;
                  return (
                    <Box key={p.name} sx={{
                      p: { xs: 4, md: 5 }, borderRadius: `${radius + 4}px`, position: 'relative',
                      bgcolor: hi ? (dark ? accent : primary) : onDark ? 'rgba(255,255,255,0.05)' : surface,
                      color: hi ? (dark ? accentText : '#fff') : 'inherit', transform: { md: hi ? 'scale(1.04)' : 'none' },
                      border: `1px solid ${hi ? 'transparent' : line}`, boxShadow: hi ? `0 24px 60px color-mix(in srgb, ${dark ? accent : primary} 30%, transparent)` : 'none',
                    }}>
                      {p.badge && <Box sx={{ position: 'absolute', top: 22, right: 22, bgcolor: hi && !dark ? accent : dark && hi ? '#000' : `color-mix(in srgb, ${accent} 15%, transparent)`, color: hi && !dark ? accentText : dark && hi ? '#fff' : accent, fontSize: 12, fontWeight: 700, px: 1.5, py: 0.5, borderRadius: 999 }}>{p.badge}</Box>}
                      <Typography sx={{ fontWeight: 700, fontSize: 20, fontFamily: font, textTransform: upper ? 'uppercase' : 'capitalize' }}>{p.name}</Typography>
                      {p.sub && <Typography sx={{ fontSize: 13, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 1.5, mt: 0.5 }}>{p.sub}</Typography>}
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, my: 3 }}>
                        <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: 50, lineHeight: 1 }}>{p.price}</Typography>
                        {p.period && <Typography sx={{ opacity: 0.6 }}>{p.period}</Typography>}
                      </Box>
                      <Box sx={{ display: 'grid', gap: 1.5, mb: 4 }}>
                        {p.features.map(f => (
                          <Box key={f} sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                            <CheckRounded sx={{ fontSize: 18, color: hi ? 'inherit' : accent }} />
                            <Typography sx={{ fontSize: 15, opacity: 0.85 }}>{f}</Typography>
                          </Box>
                        ))}
                      </Box>
                      {s.cta !== false && (
                        <Button fullWidth {...action()} sx={hi ? { ...solid, ...(dark ? { bgcolor: '#000', color: '#fff', '&:hover': { bgcolor: '#000' } } : {}) } : { ...ghost, width: '100%' }}>{p.cta || s.cta || 'Choose plan'}</Button>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        );

      case 'countdown':
        return (
          <Box key={key} component="section" id={sid(s.id || 'about')} sx={{ ...pad, ...tone }}>
            <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 5, md: 10 }, alignItems: 'center' }}>
              <Collage kind={s.art || 'stack'} h={480} icons={s.icons || []} />
              <Box>
                {s.eyebrow && <Typography sx={eyebrowSx}>{s.eyebrow}</Typography>}
                <Typography component="h2" sx={h2}>{s.title}</Typography>
                <Typography sx={{ ...muted, mt: 2.5, mb: 4 }}>{s.text}</Typography>
                <Countdown days={s.days || 18} preview={preview} accent={accent} fg={onDark ? '#fff' : primary} font={font} line={line} />
                <Box sx={{ display: 'flex', gap: 2, mt: 4.5, flexWrap: 'wrap' }}>
                  {s.cta && <Button {...action()} sx={solid}>{s.cta}</Button>}
                  {s.cta2 && <Button {...action()} sx={ghost}>{s.cta2}</Button>}
                </Box>
              </Box>
            </Box>
          </Box>
        );

      case 'schedule':
        return (
          <Box key={key} component="section" id={sid('schedule')} sx={{ ...pad, ...tone }}>
            <Box sx={inner}>
              <HeadRow s={s} />
              <Box sx={{ display: 'grid', gap: 2.5 }}>
                {s.items.map(([title, speaker, date, time], k) => (
                  <Box key={title} sx={{ display: 'grid', gridTemplateColumns: { xs: '64px 1fr', md: '80px 1fr 220px 200px' }, gap: { xs: 2, md: 4 }, alignItems: 'center', p: { xs: 2.5, md: 3 }, borderRadius: `${radius}px`, bgcolor: onDark ? 'rgba(255,255,255,0.05)' : surface, border: `1px solid ${line}` }}>
                    <Art i={k} h={s.round === false ? 64 : 'auto'} icon="person" round="50%" sx={{ aspectRatio: '1' }} />
                    <Box><Typography sx={{ fontWeight: 700, fontSize: 19, fontFamily: font }}>{title}</Typography><Typography sx={{ fontSize: 14, color: accent, mt: 0.5 }}>{speaker}</Typography></Box>
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center', opacity: 0.8 }}><CalendarMonthRounded sx={{ color: accent }} />{date}</Box>
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center', opacity: 0.8 }}><AccessTimeRounded sx={{ color: accent }} />{time}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );

      case 'blog': {
        const style = s.style || 'cards';
        return (
          <Box key={key} component="section" id={sid(s.id || 'blog')} sx={{ ...pad, ...tone }}>
            <Box sx={inner}>
              {s.row ? <HeadRow s={s} /> : <Heading s={s} />}
              {style === 'list' ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 6 }}>
                  {s.items.map(([title, date, cat], k) => (
                    <Box key={title} sx={{ display: 'flex', gap: 2.25, alignItems: 'center', py: 2.5, borderBottom: `1px solid ${line}` }}>
                      <Art i={k} h={72} round="12px" sx={{ width: 72 }} />
                      <Box>
                        <Typography sx={{ fontSize: 13, opacity: 0.6 }}>{date} · <Box component="span" sx={{ color: accent }}>{cat}</Box></Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: 17, fontFamily: font, mt: 0.5 }}>{title}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : style === 'text' ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${s.items.length}, 1fr)` }, gap: 4 }}>
                  {s.items.map(([title, date, cat, excerpt]) => (
                    <Box key={title} sx={{ borderTop: `2px solid ${accent}`, pt: 3 }}>
                      <Typography sx={{ fontSize: 13, opacity: 0.6, mb: 1.5, textTransform: 'uppercase', letterSpacing: 1.5 }}>{date}</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 22, fontFamily: font, mb: 1.5, textTransform: upper ? 'uppercase' : 'none' }}>{title}</Typography>
                      <Typography sx={{ ...muted, fontSize: 15 }}>{excerpt || cat}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${s.cols || Math.min(s.items.length, 3)}, 1fr)` }, gap: 3.5 }}>
                  {s.items.map(([title, date, cat, excerpt], k) => (
                    <Box key={title} sx={{ bgcolor: s.plain ? 'transparent' : onDark ? 'rgba(255,255,255,0.05)' : surface, borderRadius: `${radius}px`, overflow: 'hidden', boxShadow: s.plain || onDark ? 'none' : `0 12px 36px ${primary}0d` }}>
                      <Art i={k + 1} h={s.h || 230} icon={s.icon} round={s.plain ? undefined : 0}>
                        {s.play && <Play size={56} />}
                        {cat && !s.play && <Box sx={{ position: 'absolute', top: 16, left: 16, bgcolor: accent, color: accentText, fontSize: 12, fontWeight: 700, px: 1.5, py: 0.5, borderRadius: 999 }}>{cat}</Box>}
                      </Art>
                      <Box sx={{ p: s.plain ? 0 : 3, pt: s.plain ? 2.5 : 3 }}>
                        <Typography sx={{ fontSize: 13, opacity: 0.6, mb: 1 }}>{date}</Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: 19, fontFamily: font, lineHeight: 1.35, textTransform: upper ? 'uppercase' : 'none' }}>{title}</Typography>
                        {excerpt && <Typography sx={{ ...muted, fontSize: 14.5, mt: 1.25 }}>{excerpt}</Typography>}
                        {s.more && <Typography sx={{ mt: 2, fontWeight: 700, color: accent, fontSize: 14 }}>{s.more}</Typography>}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              {s.cta && !s.row && <Box sx={{ textAlign: 'center', mt: 6 }}><Button {...action()} sx={ghost}>{s.cta}</Button></Box>}
            </Box>
          </Box>
        );
      }

      case 'release':
        // Latest release: cover + vinyl on one side, tracklist on the other.
        return (
          <Box key={key} component="section" id={sid('music')} sx={{ ...pad, ...tone }}>
            <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 6, md: 10 }, alignItems: 'center' }}>
              <Box sx={{ position: 'relative', height: { xs: 320, md: 420 } }}>
                <Box sx={{ position: 'absolute', top: '6%', right: 0, width: '72%', aspectRatio: '1', borderRadius: '50%', background: `radial-gradient(circle, ${accent} 0 9%, #111 9.5% 30%, #1d1d1d 31% 33%, #111 34% 60%, #1d1d1d 61% 63%, #111 64%)` }} />
                <Art i={1} h="88%" icon="album" shade sx={{ position: 'absolute', left: 0, top: 0, width: { xs: '80%', md: '76%' }, aspectRatio: '1', height: 'auto', boxShadow: '0 30px 60px rgba(0,0,0,0.4)' }} />
              </Box>
              <Box>
                <Typography sx={eyebrowSx}>{s.eyebrow}</Typography>
                <Typography component="h2" sx={h2}>{s.title}</Typography>
                <Box sx={{ mt: 4, display: 'grid', gap: 1.5 }}>
                  {s.tracks.map(([t, len], k) => (
                    <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, pr: 2.5, borderRadius: 3, bgcolor: k === 0 ? `color-mix(in srgb, ${accent} 16%, transparent)` : onDark ? 'rgba(255,255,255,0.04)' : surface, border: `1px solid ${k === 0 ? accent : line}` }}>
                      <Art i={k + 3} h={52} icon="music" shade round="10px" sx={{ width: 52 }} />
                      <Box sx={{ flex: 1 }}><Typography sx={{ fontWeight: 700, textTransform: upper ? 'uppercase' : 'none', letterSpacing: upper ? 1 : 0 }}>{t}</Typography><Typography sx={{ fontSize: 13, opacity: 0.6 }}>{s.artist}</Typography></Box>
                      <Typography sx={{ fontSize: 14, opacity: 0.7 }}>{len}</Typography>
                      <Box sx={{ width: 38, height: 38, borderRadius: '50%', bgcolor: accent, color: accentText, display: 'grid', placeItems: 'center' }}><PlayArrowRounded /></Box>
                    </Box>
                  ))}
                </Box>
                {s.platforms && (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 3.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 600, opacity: 0.7 }}>Play on:</Typography>
                    {s.platforms.map(p => <Box key={p} sx={{ px: 2, py: 0.75, borderRadius: 999, border: `1px solid ${line}`, fontSize: 14, fontWeight: 600 }}>{p}</Box>)}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        );

      case 'albums':
        return (
          <Box key={key} component="section" id={sid(s.id || 'discography')} sx={{ ...pad, ...tone }}>
            <Box sx={inner}>
              <Heading s={s} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${s.cols || 4}, 1fr)` }, gap: 3 }}>
                {s.items.map(([title, sub], k) => (
                  <Box key={title}>
                    <Art i={k} h="auto" icon="album" shade={dark} sx={{ aspectRatio: '1', mb: 1.75 }} />
                    {s.label && <Typography sx={{ fontSize: 12, color: accent, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1.5 }}>{s.label}</Typography>}
                    <Typography sx={{ fontWeight: 700, fontSize: 17, fontFamily: font, textTransform: upper ? 'uppercase' : 'capitalize' }}>{title}</Typography>
                    <Typography sx={{ fontSize: 13.5, opacity: 0.6 }}>{sub}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );

      case 'events':
        return (
          <Box key={key} component="section" id={sid(s.id || 'events')} sx={{ ...pad, ...tone }}>
            <Box sx={inner}>
              {s.row ? <HeadRow s={s} /> : <Heading s={s} />}
              {s.tabs && (
                <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                  {s.tabs.map((t, k) => <Typography key={t} sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, fontSize: 14, color: k ? 'inherit' : accent, opacity: k ? 0.55 : 1, borderBottom: `2px solid ${k ? 'transparent' : accent}`, pb: 0.5 }}>{t}</Typography>)}
                </Box>
              )}
              <Box sx={{ display: 'grid', gap: 2 }}>
                {s.items.map(([day, month, title, place, time, cancelled]) => (
                  <Box key={title + day} sx={{ display: 'grid', gridTemplateColumns: { xs: '80px 1fr', md: '96px 1.4fr 1fr auto' }, gap: { xs: 2, md: 4 }, alignItems: 'center', p: { xs: 2.5, md: 3 }, borderRadius: `${radius}px`, bgcolor: onDark ? 'rgba(255,255,255,0.04)' : surface, border: `1px solid ${line}`, opacity: cancelled ? 0.6 : 1 }}>
                    <Box sx={{ textAlign: 'center', borderRight: `1px solid ${line}`, pr: 2 }}>
                      <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: 40, lineHeight: 1, color: accent }}>{day}</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1.5, opacity: 0.7 }}>{month}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 20, fontFamily: font, textTransform: upper ? 'uppercase' : 'none' }}>{title}</Typography>
                      <Typography sx={{ fontSize: 14, opacity: 0.65, mt: 0.5, display: { md: 'none' } }}>{place}</Typography>
                    </Box>
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                      <Typography sx={{ display: 'flex', gap: 0.75, alignItems: 'center', fontSize: 14.5 }}><PlaceOutlined sx={{ fontSize: 18, color: accent }} />{place}</Typography>
                      <Typography sx={{ display: 'flex', gap: 0.75, alignItems: 'center', fontSize: 14.5, opacity: 0.7, mt: 0.5 }}><AccessTimeRounded sx={{ fontSize: 18, color: accent }} />{time}</Typography>
                    </Box>
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.5 }}>
                      {cancelled ? (
                        <Box sx={{ px: 2.5, py: 1, borderRadius: c.btnRound ?? 999, border: '1px solid #e5484d', color: '#e5484d', fontWeight: 700, fontSize: 13, letterSpacing: 1 }}>CANCELLED</Box>
                      ) : (
                        <>
                          <Button size="small" {...action()} sx={{ ...ghost, py: 0.75, px: 2.25, fontSize: 13 }}>{s.cta2 || 'RSVP'}</Button>
                          <Button size="small" {...action()} sx={{ ...solid, py: 0.75, px: 2.25, fontSize: 13 }}>{s.cta || 'Tickets'}</Button>
                        </>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );

      case 'cta':
        return (
          <Box key={key} component="section" id={sid(s.id || 'contact')} sx={{ ...pad, py: { xs: 5, md: 7 } }}>
            <Box sx={{
              ...inner, borderRadius: `${radius + 8}px`, px: { xs: 4, md: 8 }, py: { xs: 5, md: 7 },
              bgcolor: s.tone === 'accent' ? accent : dark ? '#1a1a1a' : primary, color: s.tone === 'accent' ? accentText : '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, flexDirection: { xs: 'column', md: 'row' }, textAlign: { xs: 'center', md: 'left' },
              backgroundImage: `radial-gradient(circle at 90% 10%, color-mix(in srgb, ${s.tone === 'accent' ? '#fff' : accent} 35%, transparent) 0, transparent 40%)`,
            }}>
              <Box sx={{ maxWidth: 680 }}>
                {s.eyebrow && <Typography sx={{ ...eyebrowSx, color: s.tone === 'accent' ? 'inherit' : accent }}>{s.eyebrow}</Typography>}
                <Typography component="h2" sx={{ ...h2, fontSize: { xs: 28, md: 40 } }}>{s.title}</Typography>
                {s.text && <Typography sx={{ opacity: 0.8, mt: 1.5 }}>{s.text}</Typography>}
              </Box>
              <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                {s.phone && <Typography sx={{ fontWeight: 700, fontSize: 20, display: 'flex', alignItems: 'center', gap: 1 }}><PhoneRounded />{s.phone}</Typography>}
                {s.form ? <Box sx={{ width: { xs: '100%', md: 420 } }}><Email cta={s.cta} light /></Box> : (
                  <Button {...action()} sx={{ ...solid, ...(s.tone === 'accent' ? { bgcolor: primary, color: '#fff', '&:hover': { bgcolor: primary } } : {}), px: 4, py: 1.6 }}>{s.cta}</Button>
                )}
              </Box>
            </Box>
          </Box>
        );

      case 'offices':
        return (
          <Box key={key} component="section" id={sid('contact')} sx={{ ...pad, ...tone, py: { xs: 6, md: 8 } }}>
            <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${s.items.length}, 1fr)` }, gap: 4 }}>
              {s.items.map(([city, addr, phone]) => (
                <Box key={city} sx={{ p: 4, borderRadius: `${radius}px`, border: `1px solid ${line}` }}>
                  <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: 24, mb: 2 }}>{city}</Typography>
                  <Typography sx={{ display: 'flex', gap: 1, ...muted, mb: 1 }}><PlaceOutlined sx={{ color: accent }} />{addr}</Typography>
                  <Typography sx={{ display: 'flex', gap: 1, fontWeight: 600 }}><PhoneRounded sx={{ color: accent }} />{phone}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );

      case 'newsletter':
        if (s.art) {
          return (
            <Box key={key} component="section" id={sid('newsletter')} sx={{ ...pad, ...tone }}>
              <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 5, md: 10 }, alignItems: 'center' }}>
                <Collage kind={s.art} h={420} icons={s.icons || []} />
                <Box>
                  {s.eyebrow && <Typography sx={eyebrowSx}>{s.eyebrow}</Typography>}
                  <Typography component="h2" sx={h2}>{s.title}</Typography>
                  <Typography sx={{ ...muted, mt: 2, mb: 4 }}>{s.text}</Typography>
                  <Email cta={s.cta} light={onDark} center={false} />
                </Box>
              </Box>
            </Box>
          );
        }
        return (
          <Box key={key} component="section" id={sid('newsletter')} sx={{ ...pad, ...tone }}>
            <Box sx={{ ...inner, maxWidth: 760, textAlign: 'center' }}>
              <MailOutlineRounded sx={{ fontSize: 44, color: s.tone === 'accent' ? 'inherit' : accent, mb: 2 }} />
              <Typography component="h2" sx={h2}>{s.title}</Typography>
              {s.text && <Typography sx={{ ...muted, mt: 1.5, mb: 4 }}>{s.text}</Typography>}
              <Email cta={s.cta} light={onDark} />
            </Box>
          </Box>
        );

      case 'contact':
        return (
          <Box key={key} component="section" id={sid('contact')} sx={{ ...pad, ...tone }}>
            <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.1fr' }, gap: { xs: 5, md: 10 } }}>
              <Box>
                {s.eyebrow && <Typography sx={eyebrowSx}>{s.eyebrow}</Typography>}
                <Typography component="h2" sx={h2}>{s.title}</Typography>
                {s.text && <Typography sx={{ ...muted, mt: 2 }}>{s.text}</Typography>}
                <Box sx={{ display: 'grid', gap: 2, mt: 4 }}>
                  {[[PhoneRounded, s.phone], [MailOutlineRounded, s.email], [PlaceOutlined, s.address]].filter(([, v]) => v).map(([Ic, v]) => (
                    <Box key={v} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}><Ic sx={{ color: accent }} /><Typography sx={{ fontWeight: 500 }}>{v}</Typography></Box>
                  ))}
                </Box>
              </Box>
              <Box sx={{ p: { xs: 3, md: 4.5 }, borderRadius: `${radius + 4}px`, bgcolor: onDark ? 'rgba(255,255,255,0.05)' : surface, boxShadow: onDark ? 'none' : `0 20px 50px ${primary}12`, display: 'grid', gap: 2 }}>
                {['Your name', 'Email address', 'Phone', 'Tell us about your project'].map((ph, k) => (
                  <InputBase key={ph} placeholder={ph} multiline={k === 3} minRows={k === 3 ? 4 : 1} inputProps={{ 'aria-label': ph, tabIndex: preview ? -1 : 0 }}
                    sx={{ px: 2, py: 1.5, borderRadius: 2, border: `1px solid ${line}`, color: 'inherit', fontFamily: body, fontSize: 15 }} />
                ))}
                <Button {...action()} sx={{ ...solid, justifySelf: 'start' }}>{s.cta || 'Send message'}</Button>
              </Box>
            </Box>
          </Box>
        );

      case 'footer':
        return renderFooter(s, key);

      default:
        return null;
    }
  };

  const renderHero = (s, key) => {
    const v = s.variant || 'split';
    const Buttons = ({ light }) => (s.cta || s.cta2) ? (
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'inherit' }}>
        {s.cta && <Button {...action()} sx={{ ...solid, px: 4, py: 1.5 }}>{s.cta}</Button>}
        {s.cta2 && <Button {...action()} sx={{ ...ghost, px: 4, py: 1.4, color: light ? '#fff' : 'inherit' }}>{s.cta2}</Button>}
      </Box>
    ) : null;
    const Title = ({ size = { xs: 40, md: 72 }, sx }) => (
      <Typography component="h1" sx={{ ...h2, fontSize: size, lineHeight: 1.04, ...sx }}>{s.title}</Typography>
    );

    if (v === 'slider') {
      // Full-width slide with dark image backdrop and slide controls.
      return (
        <Box key={key} component="section" id={sid('home')} sx={{ position: 'relative', minHeight: { xs: 560, md: s.h || 720 }, display: 'flex', alignItems: 'center', color: '#fff', overflow: 'hidden', px: { xs: 3, md: 8 }, pt: s.overlayNav ? 12 : 0 }}>
          <Box sx={{ position: 'absolute', inset: 0, background: `linear-gradient(115deg, ${dark ? '#050505' : primary} 0%, color-mix(in srgb, ${dark ? '#050505' : primary} 85%, ${accent}) 55%, color-mix(in srgb, ${accent} 45%, #000) 100%)` }} />
          {s.art !== false && (
            <Box sx={{ position: 'absolute', right: { xs: '-30%', md: '4%' }, bottom: 0, width: { xs: '90%', md: '42%' }, height: '88%', opacity: { xs: 0.25, md: 1 } }}>
              <Collage kind={s.art || 'cutout'} h="100%" icons={s.icons || []} />
            </Box>
          )}
          <Box sx={{ ...inner, width: '100%', zIndex: 1 }}>
            <Box sx={{ maxWidth: 640 }}>
              {s.eyebrow && <Typography sx={{ ...eyebrowSx, fontSize: 14 }}>{s.eyebrow}</Typography>}
              <Title size={{ xs: 42, md: 76 }} />
              {s.text && <Typography sx={{ fontSize: 18, opacity: 0.8, mt: 3, mb: 4.5, maxWidth: 520, lineHeight: 1.7 }}>{s.text}</Typography>}
              <Buttons light />
              {s.socials && <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 5 }}><Typography sx={{ fontWeight: 600, fontSize: 14 }}>Follow us:</Typography><Socials n={4} size={34} /></Box>}
            </Box>
          </Box>
          <Box sx={{ position: 'absolute', bottom: 32, left: { xs: 24, md: 64 }, display: 'flex', alignItems: 'center', gap: 3, zIndex: 1 }}>
            {s.numbered ? ['01', '02', '03'].map((n, k) => <Typography key={n} sx={{ fontWeight: 700, opacity: k ? 0.45 : 1, borderBottom: k ? 0 : `2px solid ${accent}`, pb: 0.5 }}>{n}</Typography>) : <Dots light />}
          </Box>
          {s.arrows !== false && (
            <Box sx={{ position: 'absolute', right: { xs: 24, md: 64 }, bottom: 28, display: { xs: 'none', md: 'flex' }, gap: 1.5, zIndex: 1 }}>
              {[ArrowBackRounded, ArrowForwardRounded].map((Ic, k) => <Box key={k} sx={{ width: 50, height: 50, borderRadius: '50%', border: '1px solid #ffffff55', display: 'grid', placeItems: 'center' }}><Ic /></Box>)}
            </Box>
          )}
        </Box>
      );
    }

    if (v === 'fullscreen') {
      // Big centred statement over a dark backdrop with a scroll cue.
      return (
        <Box key={key} component="section" id={sid('home')} sx={{ position: 'relative', minHeight: { xs: 560, md: 760 }, display: 'grid', placeItems: 'center', textAlign: 'center', px: 3, overflow: 'hidden', color: '#fff', pt: s.overlayNav ? 10 : 0 }}>
          <Art i={4} h="100%" icon={s.icon} shade round={0} sx={{ position: 'absolute', inset: 0 }} />
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)' }} />
          <Box sx={{ position: 'relative', maxWidth: 1000 }}>
            {s.eyebrow && <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: { xs: 56, md: 140 }, lineHeight: 0.9, color: accent, letterSpacing: '-0.04em' }}>{s.eyebrow}</Typography>}
            <Title size={{ xs: 30, md: 56 }} sx={{ mt: 2 }} />
            {s.text && <Typography sx={{ fontSize: 18, opacity: 0.8, mt: 3, mx: 'auto', maxWidth: 560 }}>{s.text}</Typography>}
          </Box>
          <Box sx={{ position: 'absolute', bottom: 36, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, fontSize: 12, letterSpacing: 3, textTransform: 'uppercase', opacity: 0.8 }}>
            Scroll<KeyboardArrowDownRounded sx={{ color: accent }} />
          </Box>
        </Box>
      );
    }

    if (v === 'album') {
      // New album launch: big title, release date, singer cutout and badges.
      return (
        <Box key={key} component="section" id={sid('home')} sx={{ position: 'relative', overflow: 'hidden', px: { xs: 3, md: 8 }, pt: { xs: 14, md: 16 }, pb: { xs: 0, md: 0 }, minHeight: { md: 760 } }}>
          <Box sx={{ position: 'absolute', width: 640, height: 640, borderRadius: '50%', right: '8%', top: '10%', background: `radial-gradient(circle, color-mix(in srgb, ${accent} 55%, transparent) 0%, transparent 65%)` }} />
          <Typography aria-hidden sx={{ position: 'absolute', left: '50%', bottom: -30, transform: 'translateX(-50%)', fontFamily: font, fontWeight: 700, fontSize: { xs: 140, md: 320 }, lineHeight: 1, color: 'transparent', WebkitTextStroke: `1px color-mix(in srgb, ${accent} 40%, transparent)`, whiteSpace: 'nowrap', textTransform: 'uppercase' }}>{c.brand}</Typography>
          <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: 4, alignItems: 'end' }}>
            <Box sx={{ pb: { md: 14 } }}>
              <Typography sx={{ ...eyebrowSx, fontSize: 15, letterSpacing: 6 }}>{s.eyebrow}</Typography>
              <Title size={{ xs: 56, md: 120 }} sx={{ color: accent, lineHeight: 0.92 }} />
              <Typography sx={{ fontFamily: font, fontSize: 24, letterSpacing: 4, mt: 2, textTransform: 'uppercase' }}>{s.date}</Typography>
              <Typography sx={{ ...muted, mt: 2, maxWidth: 440 }}>{s.text}</Typography>
              <Box sx={{ mt: 4 }}><Buttons /></Box>
              <Box sx={{ display: 'flex', gap: 1.5, mt: 4 }}>
                {['Explicit', 'Hi-Res', 'Spatial'].map(b => <Box key={b} sx={{ px: 1.5, py: 0.5, border: `1px solid ${line}`, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', opacity: 0.7 }}>{b}</Box>)}
              </Box>
            </Box>
            <Box sx={{ height: { xs: 380, md: 640 }, position: 'relative' }}>
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'end center' }}>
                <PersonRounded sx={{ fontSize: { xs: 420, md: 720 }, color: `color-mix(in srgb, ${accent} 30%, #1a1a1a)`, mb: { xs: -6, md: -10 } }} />
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }

    if (v === 'name') {
      // Artist name split across the screen over a full-bleed photo.
      return (
        <Box key={key} component="section" id={sid('home')} sx={{ position: 'relative', minHeight: { xs: 600, md: 820 }, overflow: 'hidden', color: '#fff', display: 'grid', placeItems: 'center' }}>
          <Box sx={{ position: 'absolute', inset: 0, background: `radial-gradient(circle at 50% 60%, color-mix(in srgb, ${accent} 30%, #111) 0%, #050505 65%)` }} />
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'end center' }}>
            <PersonRounded sx={{ fontSize: { xs: 480, md: 820 }, color: '#1f1f1f', mb: { xs: -8, md: -14 } }} />
          </Box>
          <Box sx={{ position: 'relative', width: '100%', px: { xs: 3, md: 8 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            {s.title.split(' ').map((w, k) => (
              <Typography key={w} component={k ? 'span' : 'h1'} sx={{ fontFamily: font, fontWeight: 700, fontSize: { xs: 64, md: 190 }, lineHeight: 0.85, textTransform: 'uppercase', letterSpacing: '-0.02em', color: k ? 'transparent' : '#fff', WebkitTextStroke: k ? `2px ${accent}` : 0 }}>{w}</Typography>
            ))}
          </Box>
          <Typography sx={{ position: 'absolute', bottom: 48, left: { xs: 24, md: 64 }, fontFamily: font, fontSize: { xs: 20, md: 28 }, color: accent, textTransform: 'uppercase', letterSpacing: 3 }}>{s.text}</Typography>
          <Box sx={{ position: 'absolute', bottom: 48, right: { xs: 24, md: 64 }, display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 58, height: 58, borderRadius: '50%', bgcolor: accent, color: accentText, display: 'grid', placeItems: 'center' }}><PlayArrowRounded sx={{ fontSize: 30 }} /></Box>
            <Box><Typography sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>{s.track}</Typography><Typography sx={{ fontSize: 13, opacity: 0.6 }}>Now playing</Typography></Box>
          </Box>
        </Box>
      );
    }

    if (v === 'statement') {
      // Personal portfolio opener: huge statement, name, role and portrait.
      return (
        <Box key={key} component="section" id={sid('home')} sx={{ ...pad, pt: { xs: 6, md: 10 } }}>
          <Box sx={inner}>
            <Title size={{ xs: 46, md: 110 }} sx={{ maxWidth: 1100, letterSpacing: '-0.045em' }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '200px 1fr 1fr' }, gap: { xs: 3, md: 6 }, mt: { xs: 6, md: 9 }, alignItems: 'start' }}>
              <Art i={2} h={200} icon="person" round="50%" sx={{ width: 200 }} />
              <Box>
                <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: 26 }}>{s.name}</Typography>
                <Typography sx={{ color: accent, fontWeight: 600, mt: 0.5 }}>{s.role}</Typography>
              </Box>
              <Box>
                <Typography sx={{ ...muted }}>{s.text}</Typography>
                {s.text2 && <Typography sx={{ ...muted, mt: 2 }}>{s.text2}</Typography>}
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }

    if (v === 'gallery') {
      // Minimal photographer intro: a quiet two-line title.
      return (
        <Box key={key} component="section" id={sid('home')} sx={{ ...pad, pt: { xs: 6, md: 10 }, pb: { xs: 3, md: 5 }, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: FONTS.serif, fontStyle: 'italic', fontSize: { xs: 20, md: 26 }, opacity: 0.6 }}>{s.eyebrow}</Typography>
          <Title size={{ xs: 40, md: 76 }} sx={{ mt: 1 }} />
        </Box>
      );
    }

    if (v === 'conference') {
      // Event hero: title, theme line, date and venue with ticket CTA.
      return (
        <Box key={key} component="section" id={sid('home')} sx={{ position: 'relative', overflow: 'hidden', px: { xs: 3, md: 8 }, pt: { xs: 14, md: 18 }, pb: { xs: 8, md: 12 }, color: '#fff', background: `linear-gradient(140deg, ${primary} 0%, color-mix(in srgb, ${primary} 70%, ${accent}) 100%)` }}>
          {[[520, '-8%', '-12%'], [340, '70%', '50%'], [180, '82%', '8%']].map(([size, left, top], k) => (
            <Box key={k} sx={{ position: 'absolute', width: size, height: size, borderRadius: '50%', left, top, border: `2px solid color-mix(in srgb, ${accent} ${50 - k * 10}%, transparent)`, bgcolor: k === 2 ? accent : 'transparent', opacity: k === 2 ? 0.8 : 1 }} />
          ))}
          <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 6, alignItems: 'center' }}>
            <Box>
              <Typography sx={{ ...eyebrowSx, color: accent }}>{s.eyebrow}</Typography>
              <Title size={{ xs: 52, md: 104 }} sx={{ lineHeight: 0.95 }} />
              <Typography sx={{ fontSize: { xs: 20, md: 26 }, mt: 2.5, fontFamily: font, opacity: 0.9 }}>{s.subtitle}</Typography>
              <Typography sx={{ opacity: 0.75, mt: 2, maxWidth: 520, lineHeight: 1.7 }}>{s.text}</Typography>
              <Box sx={{ display: 'flex', gap: { xs: 2, md: 4 }, mt: 4, mb: 4.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><CalendarMonthRounded sx={{ color: accent }} /><Typography sx={{ fontWeight: 600 }}>{s.date}</Typography></Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><PlaceOutlined sx={{ color: accent }} /><Typography sx={{ fontWeight: 600 }}>{s.place}</Typography></Box>
              </Box>
              <Buttons light />
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}><Collage kind="circle" h={500} icons={s.icons || ['mic', 'event', 'team']} /></Box>
          </Box>
        </Box>
      );
    }

    // Default split hero: copy on one side, artwork on the other.
    const heroDark = s.tone === 'band';
    return (
      <Box key={key} component="section" id={sid('home')} sx={{
        position: 'relative', overflow: 'hidden', px: { xs: 3, md: 8 }, pt: { xs: s.overlayNav ? 14 : 6, md: s.overlayNav ? 18 : 10 }, pb: { xs: 7, md: 10 },
        ...(heroDark ? { bgcolor: primary, color: '#fff' } : s.tone === 'alt' ? toneSx('alt') : {}),
      }}>
        {s.blob !== false && <Box sx={{ position: 'absolute', width: 620, height: 620, borderRadius: '50%', right: '-10%', top: '-20%', background: `radial-gradient(circle, color-mix(in srgb, ${accent} 22%, transparent) 0%, transparent 70%)` }} />}
        <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: s.center ? '1fr' : '1.05fr 1fr' }, gap: { xs: 5, md: 8 }, alignItems: 'center', textAlign: s.center ? 'center' : 'left' }}>
          <Box sx={{ maxWidth: s.center ? 900 : 'none', mx: s.center ? 'auto' : 0, justifyContent: s.center ? 'center' : 'flex-start', display: 'flex', flexDirection: 'column' }}>
            {s.eyebrow && <Typography sx={{ ...eyebrowSx, fontSize: 14 }}>{s.eyebrow}</Typography>}
            <Title size={s.size || { xs: 40, md: 68 }} />
            {s.text && <Typography sx={{ fontSize: 18, ...muted, mt: 3, mb: 4.5, maxWidth: 560, mx: s.center ? 'auto' : 0 }}>{s.text}</Typography>}
            <Box sx={{ display: 'flex', justifyContent: s.center ? 'center' : 'flex-start' }}><Buttons light={heroDark} /></Box>
            {s.tabs && (
              <Box sx={{ display: 'flex', gap: 3, mt: 5, flexWrap: 'wrap' }}>
                {s.tabs.map((t, k) => <Typography key={t} sx={{ fontWeight: 600, fontSize: 15, pb: 0.75, borderBottom: `2px solid ${k ? line : accent}`, opacity: k ? 0.6 : 1 }}>{t}</Typography>)}
              </Box>
            )}
            {s.chips && (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 5, flexWrap: 'wrap', justifyContent: s.center ? 'center' : 'flex-start' }}>
                {s.chips.map(([label, icon]) => {
                  const Ic = ICONS[icon] || BoltRounded;
                  return <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.1, borderRadius: 3, bgcolor: heroDark || dark ? 'rgba(255,255,255,0.06)' : '#fff', boxShadow: heroDark || dark ? 'none' : `0 10px 28px ${primary}12`, fontWeight: 600, fontSize: 14 }}><Ic sx={{ color: accent, fontSize: 20 }} />{label}</Box>;
                })}
              </Box>
            )}
          </Box>
          {!s.center && <Box sx={{ display: { xs: s.hideArtMobile ? 'none' : 'block', md: 'block' } }}><Collage kind={s.art || 'single'} h={s.h || 520} icons={s.icons || []} /></Box>}
        </Box>
      </Box>
    );
  };

  const renderFooter = (s, key) => {
    const copy = `© ${new Date().getFullYear()} ${c.brand}. All rights reserved.`;
    const fdark = s.light ? false : true;
    const fsx = fdark ? { bgcolor: dark ? '#050505' : primary, color: '#fff' } : { bgcolor: bg, color: fg, borderTop: `1px solid ${line}` };
    if (s.style === 'minimal') {
      return (
        <Box key={key} component="footer" sx={{ ...fsx, px: { xs: 3, md: 8 }, py: 4, fontFamily: body }}>
          <Box sx={{ ...inner, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Typography sx={{ fontSize: 14, opacity: 0.65 }}>{copy}</Typography>
            {s.socials !== false && <Socials n={s.socials || 3} size={34} />}
          </Box>
        </Box>
      );
    }
    if (s.style === 'centered') {
      return (
        <Box key={key} component="footer" id={sid('contact-info')} sx={{ ...fsx, px: { xs: 3, md: 8 }, pt: { xs: 7, md: 9 }, pb: 4, textAlign: 'center', fontFamily: body }}>
          <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: 30, textTransform: upper ? 'uppercase' : 'none', letterSpacing: upper ? 2 : 0 }}>{c.brand}</Typography>
          {s.about && <Typography sx={{ opacity: 0.65, mt: 1.5, maxWidth: 520, mx: 'auto' }}>{s.about}</Typography>}
          {s.links && (
            <Box sx={{ display: 'flex', gap: { xs: 2, md: 4 }, justifyContent: 'center', flexWrap: 'wrap', mt: 4 }}>
              {s.links.map(l => <Typography key={l} sx={{ fontSize: 14.5, opacity: 0.75, textTransform: upper ? 'uppercase' : 'none', letterSpacing: upper ? 1.2 : 0 }}>{l}</Typography>)}
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><Socials n={s.socials || 4} size={40} /></Box>
          <Typography sx={{ fontSize: 13.5, opacity: 0.5, mt: 5, pt: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>{copy}</Typography>
        </Box>
      );
    }
    return (
      <Box key={key} component="footer" id={sid('contact-info')} sx={{ ...fsx, px: { xs: 3, md: 8 }, pt: { xs: 7, md: 10 }, pb: 3, fontFamily: body }}>
        <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: `1.4fr repeat(${(s.columns || []).length + (s.contact ? 1 : 0) + (s.form ? 1 : 0)}, 1fr)` }, gap: { xs: 4, md: 6 } }}>
          <Box>
            <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: 26, mb: 2, textTransform: upper ? 'uppercase' : 'none' }}>{c.brand}</Typography>
            <Typography sx={{ opacity: 0.65, lineHeight: 1.75, mb: 3, maxWidth: 320 }}>{s.about}</Typography>
            <Socials n={4} size={36} />
          </Box>
          {(s.columns || []).map(([title, items]) => (
            <Box key={title}>
              <Typography sx={{ fontWeight: 700, mb: 2.5, fontSize: 17 }}>{title}</Typography>
              <Box sx={{ display: 'grid', gap: 1.4 }}>{items.map(it => <Typography key={it} sx={{ opacity: 0.65, fontSize: 14.5 }}>{it}</Typography>)}</Box>
            </Box>
          ))}
          {s.contact && (
            <Box>
              <Typography sx={{ fontWeight: 700, mb: 2.5, fontSize: 17 }}>Contact</Typography>
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                {s.contact.map(([kind, v]) => {
                  const Ic = kind === 'phone' ? PhoneRounded : kind === 'mail' ? MailOutlineRounded : PlaceOutlined;
                  return <Box key={v} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', opacity: 0.8, fontSize: 14.5 }}><Ic sx={{ fontSize: 18, color: accent, mt: 0.25 }} />{v}</Box>;
                })}
              </Box>
            </Box>
          )}
          {s.form && (
            <Box>
              <Typography sx={{ fontWeight: 700, mb: 2.5, fontSize: 17 }}>{s.form}</Typography>
              <Typography sx={{ opacity: 0.65, fontSize: 14.5, mb: 2 }}>Get updates and offers in your inbox.</Typography>
              <Email cta="Join" light maxWidth={320} center={false} />
            </Box>
          )}
        </Box>
        <Box sx={{ ...inner, mt: 7, pt: 3, borderTop: '1px solid rgba(255,255,255,0.12)', display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 13.5, opacity: 0.55 }}>{copy}</Typography>
          <Typography sx={{ fontSize: 13.5, opacity: 0.55 }}>Privacy · Terms</Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ bgcolor: bg, color: fg, fontFamily: body, minWidth: 0, position: 'relative', overflow: 'hidden' }}>
      {c.sections.map(render)}
    </Box>
  );
};

// Days / hours / minutes / seconds to an event a set number of days out.
// Thumbnails show a still value so the gallery does not re-render.
const Countdown = ({ days, preview, accent, fg, font, line }) => {
  const [target] = useState(() => Date.now() + days * 86400000 + 7 * 3600000 + 42 * 60000);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (preview) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [preview]);
  const left = Math.max(0, target - now) / 1000;
  const parts = [['Days', left / 86400], ['Hours', (left / 3600) % 24], ['Minutes', (left / 60) % 60], ['Seconds', left % 60]];
  return (
    <Box role="timer" aria-label="Time until the event" sx={{ display: 'flex', gap: { xs: 1.25, md: 2 } }}>
      {parts.map(([label, v]) => (
        <Box key={label} sx={{ width: { xs: 70, md: 96 }, py: 2, textAlign: 'center', borderRadius: 3, border: `1px solid ${line}` }}>
          <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: { xs: 26, md: 38 }, color: fg, lineHeight: 1.1 }}>{String(Math.floor(v)).padStart(2, '0')}</Typography>
          <Typography sx={{ fontSize: 11.5, letterSpacing: 1, textTransform: 'uppercase', color: accent }}>{label}</Typography>
        </Box>
      ))}
    </Box>
  );
};

export default CreativeSite;
